import * as React from 'react';
import styled from 'styled-components';
import { FormsReportType } from '../api/reports';
import FormReportItem from '../molecules/FormReportItem';
import { SkippedInspection } from '../pages/Reports/FormsReport';

// One line per jurisdiction, with a count, so the inspector can see at a glance
// what the day contains that this modal cannot print.
const summariseSkipped = (skipped: Array<SkippedInspection>) => {
  const byJurisdiction = new Map<string, number>();
  skipped.forEach(item => {
    const name = item?.jurisdiction || 'No jurisdiction on the permit';
    byJurisdiction.set(name, (byJurisdiction.get(name) || 0) + 1);
  });
  return Array.from(byJurisdiction.entries()).sort((a, b) => a[0].localeCompare(b[0]));
};

const FormsReportList = React.forwardRef(
  (
    {
      forms,
      skipped,
      setSelectedFormsReports,
      selectedFormsReports,
    }: Props,
    ref,
  ) => {
    // Rendered above the list AND in place of it, so a day where nothing could
    // be printed still explains itself instead of showing a bare error.
    const notice = skipped?.length ? (
      <SkippedNotice>
        <SkippedTitle>
          {skipped.length === 1
            ? '1 scheduled inspection has no printable form here'
            : `${skipped.length} scheduled inspections have no printable form here`}
        </SkippedTitle>
        <SkippedList>
          {summariseSkipped(skipped).map(([jurisdiction, count]) => (
            <li key={jurisdiction}>
              {jurisdiction}
              {count > 1 ? ` — ${count} inspections` : ''}
            </li>
          ))}
        </SkippedList>
        <SkippedHint>
          These use a form held in the inspection form builder, which this report cannot print. Open
          the inspection to fill and print it.
        </SkippedHint>
      </SkippedNotice>
    ) : null;

    if (!forms?.length) {
      return (
        <EmptyWrapper>
          {notice}
          <InfoText>
            {skipped?.length
              ? 'There is nothing to print for this date.'
              : 'No project was found with selected Jurisdiction.'}
          </InfoText>
        </EmptyWrapper>
      );
    }
    return (
      <ListWrapper ref={ref}>
        {notice}
        {forms?.map(form => (
          <FormReportItem
            key={`${form?.name}${form?.id}`}
            form={form}
            setSelectedFormsReports={
              setSelectedFormsReports
            }
            selectedFormsReports={selectedFormsReports}
          />
        ))}
      </ListWrapper>
    );
  },
);

interface Props {
  forms?: Array<FormsReportType>;
  skipped?: Array<SkippedInspection>;
  setSelectedFormsReports: (data: any) => void;
  selectedFormsReports: Array<FormsReportType>;
}

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media print {
    position: absolute;
    top:0;
    display: block;
    height: auto;
  }
`;

const InfoText = styled.div`
  margin: 0 auto;
  font-size: 24px;
  color: var(--mainText);
`;

const EmptyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

// Amber rather than red: a jurisdiction whose form lives in the builder is a
// known limitation, not a failure.
const SkippedNotice = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 18px;
  border: 1px solid var(--warning-100);
  border-radius: 10px;
  background: var(--warning-50);
  box-sizing: border-box;
  width: 100%;

  /* The list is what gets printed; this is screen-only guidance. */
  @media print {
    display: none;
  }
`;

const SkippedTitle = styled.div`
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--warning-700);
`;

const SkippedList = styled.ul`
  margin: 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 0.875rem;
  color: var(--mainText);
`;

const SkippedHint = styled.div`
  font-size: 0.8125rem;
  line-height: 1.45;
  color: var(--neutral-600);
`;

export default FormsReportList;
